import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { Indicator } from '../../../models/indicator';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { IndicatorData } from '../../../models/indicator-data';
import { DataLayerType } from '../../../models/data-layer-type';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-fast-switch-indicator',
  standalone: true,
  imports: [TieredMenuModule, NgClass],
  templateUrl: './fast-switch-indicator.component.html',
  styleUrl: './fast-switch-indicator.component.scss',
})
export class FastSwitchIndicatorComponent implements OnInit, OnChanges {
  readonly indicatorStore = inject(IndicatorStore);
  readonly layerStore = inject(DataLayerStore);
  readonly geoStore = inject(GeoDataStore);
  readonly typeStore = inject(DataLayerTypeStore);
  readonly dataStore = inject(IndicatorDataStore);

  @Input() readonly = false;
  @Input() indicatorId!: string;
  @Input() typeId!: string;
  @Input() indicatorIds!: string[];
  @Input() activeGroup = '';
  @Output() changeIndicator = new EventEmitter<{
    indicator: Indicator;
    typeId: string;
  }>();
  @Output() changeGroup = new EventEmitter<{
    group: string;
    dataLayerId: string;
  }>();

  indicator!: Indicator;
  menu: MenuItem[] = [];
  dataLayerType?: DataLayerType;
  groupMenu: MenuItem | null = null;
  indicatorMenu: MenuItem[] = [];

  ngOnInit() {
    this.init();
  }

  /**
   * Responds to changes in the input properties bound to the component. Triggered when Angular detects changes in data-bound input properties.
   *
   * @param {SimpleChanges} changes - An object that holds the current and previous value of changed properties.
   * @return {void} - Does not return anything.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['indicatorIds'] ||
      changes['dataLayerId'] ||
      changes['activeGroup']
    ) {
      this.init();
    }
  }

  /**
   * Initializes the component by setting up various data layers, menus, and configurations based on provided IDs and stored data.
   *
   * @return {void} Returns nothing.
   */
  init(): void {
    this.indicator = this.indicatorStore.getById(this.indicatorId);
    if (!this.indicatorIds.length) return;

    const availableData = this.dataStore.getGroupedByDataLayer(
      this.indicatorIds
    );

    if (this.indicator)
      this.dataLayerType = this.typeStore.getById(this.typeId);

    if (this.dataLayerType) {
      const dataLayer = this.layerStore.getLatestByParent(
        this.dataLayerType._id
      );

      if (dataLayer.geoData) {
        this.groupMenu = this.geoStore.getGeoGroupMenu(
          dataLayer.geoData,
          dataLayer._id,
          true,
          (event: { group: string; dataLayerId: string }) => {
            this.activeGroup = event.group;
            this.changeGroup.emit(event);
          }
        );
      }
    }

    this.buildLayerMenu(availableData);
  }

  /**
   * Builds a menu for layer selection based on the available data and existing type information.
   * Updates the `indicatorMenu` property with the constructed menu structure.
   *
   * @param {Record<string, Record<string, IndicatorData>>} availableData - A record of available indicators categorized by their type IDs, where each type ID maps to another record of indicator data.
   * @return {void} This method does not return a value.
   */
  private buildLayerMenu(
    availableData: Record<string, Record<string, IndicatorData>>
  ): void {
    this.indicatorMenu = [];

    if (this.groupMenu) {
      this.indicatorMenu.push(this.groupMenu);
      this.indicatorMenu.push({ separator: true });
    }

    for (const typeId in availableData) {
      const typeIndicators = availableData[typeId];
      const type = this.typeStore.getById(typeId);
      const typeIndicatorItems: MenuItem[] = [];

      for (const indicatorId in typeIndicators) {
        const indicator = this.indicatorStore.getById(
          typeIndicators[indicatorId].indicator
        );
        if (!indicator) continue;
        typeIndicatorItems.push({
          icon: 'fas fa-ruler-vertical',
          label: indicator.name,
          id: indicatorId,
          command: (event: MenuItemCommandEvent) => {
            this.changeIndicator.emit({ indicator, typeId });
          },
        });
      }

      this.indicatorMenu.push({
        icon:
          type.type === 'Point' ? 'fas fa-location-dot' : 'fa-regular fa-map',
        label: type.name,
        items: typeIndicatorItems,
      });
    }
  }
}
